/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from "react";

// Third party
import axios from "axios";
import bbox from "@turf/bbox";
import { renderToStaticMarkup } from "react-dom/server";

// React Leaflet
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
  Popup,
  ZoomControl,
} from "react-leaflet";
import { TiledMapLayer } from "react-esri-leaflet";

// Loading
import LoadingOverlay from "react-loading-overlay";
import BounceLoader from "react-spinners/BounceLoader";
import LinearProgress from "@mui/material/LinearProgress";

// Constants
import {
  COLOR_LINES,
  POPUP_GROUPS,
  SUMMARY_VARIABLES_HPMS,
  commodity_val_consts,
  yearOptions,
  geographic_options,
  mapFocusCategories,
} from "../common/constants";

// Context
import { useGlobalState, useGlobalUpdater } from "../common/GlobalContext";

// Material UI  core
import { makeStyles } from "@material-ui/core/styles";
import Icon from "@material-ui/core/Icon";
import IconButton from "@material-ui/core/IconButton";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";

// Material UI  MUI v5
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";

// MUI Icons
import ConstructionIcon from "@mui/icons-material/Construction";
import FlightIcon from "@mui/icons-material/Flight";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BusinessIcon from "@mui/icons-material/Business";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AnchorIcon from "@mui/icons-material/Anchor";
import TollIcon from "@mui/icons-material/Toll";
import LocalParkingIcon from "@mui/icons-material/LocalParking";

// Local components
import { QuickInfoControl } from "./QuickInfoControl";
import MapFilter from "./MapFilter";
import TollPlazasLayer from "./other_layers/TollPlazasLayer";
import PortsLayer from "./other_layers/PortsLayer";
import OtherTerminalsLayer from "./other_layers/OtherTerminalsLayer";
import IntermodalFacilityLayer from "./other_layers/IntermodalFacilityLayer";
import CargoAirportsLayer from "./other_layers/CargoAirportsLayer";
import AutoTerminalsLayer from "./other_layers/AutoTerminalsLayer";
import DistributionCentersLayer from "./other_layers/DistributionCentersLayer";
import TruckWeighInspStationsLayer from "./other_layers/TruckWeighInspStationsLayer";
import AirportsLayer from "./other_layers/AirportsLayer";
import CountyBoundaryLayer from "./other_layers/CountyBoundaryLayer";
import DistrictBoundaryLayer from "./other_layers/DistrictBoundaryLayer";
import TruckParkingLayer from "./other_layers/TruckParkingLayer";
import CtpLayer from "./other_layers/CtpLayer";
import CrabLayer from "./other_layers/CrabLayer";
import ShaProjectsLayer from "./other_layers/ShaProjectsLayer";
import LandUse2010 from "./other_layers/LandUse2010";

const useStyles = makeStyles((theme) => ({
  linearProgress: {
    marginTop: "5px",
  },
}));

function LeafletMapState(props) {
  const { openDrawer,mode } = props;
  const classes = useStyles();
  const query = useGlobalState();
  const dispatch = useGlobalUpdater();

  const [hpmsGeo, setHpmsGeo] = useState({
    type: "FeatureCollection",
    features: [],
  });

  const [fullHPMSdata, setFullHPMSdata] = useState([]);
  // setGeoJsonKey causes re-rendering of map to include selected segment colors
  const [geoJsonKey, setGeoJsonKey] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const [info, setInfo] = useState({});
  const hpmsColorContainer = useRef("#ffffff");
  const [checked, setChecked] = useState({
    truck: false,
    directional: false,
  });
  const [map, setMap] = useState(null);
// "Roads" and "Streets" are the default-checked layers on load.
const [activeOverlays, setActiveOverlays] = useState(["Roads"]);
const [activeBaseLayer, setActiveBaseLayer] = useState("Streets");
  const lineWeights = { normal: 3, selected: 8, minimized: 1 };
  const hpmsWeightContainer = useRef(lineWeights.normal);

  const handleCheckChange = (event) => {
    setChecked({ ...checked, [event.target.name]: event.target.checked });
  };
  const [directionalSwitch, setDirectionalSwitch] = useState(false);

  useEffect(() => {
  if (!map) return;
  const onOverlayAdd = (e) =>
    setActiveOverlays((prev) => (prev.includes(e.name) ? prev : [...prev, e.name]));
  const onOverlayRemove = (e) =>
    setActiveOverlays((prev) => prev.filter((n) => n !== e.name));
  const onBaseChange = (e) => setActiveBaseLayer(e.name);

  map.on("overlayadd", onOverlayAdd);
  map.on("overlayremove", onOverlayRemove);
  map.on("baselayerchange", onBaseChange);
  return () => {
    map.off("overlayadd", onOverlayAdd);
    map.off("overlayremove", onOverlayRemove);
    map.off("baselayerchange", onBaseChange);
  };
}, [map]);

  function setBounds(fc) {
    let mapBounds_bbox = bbox({ type: "FeatureCollection", features: fc });
    if (mapBounds_bbox[1] !== Infinity)
      setMapBounds([
        [mapBounds_bbox[1], mapBounds_bbox[0]],
        [mapBounds_bbox[3], mapBounds_bbox[2]],
      ]);
  }

  function setCommodityValueDir(f_system, aadt_combi, aadt_singl) {
    let commodityvalue = 0;
    if ((f_system === 1)) {
      commodityvalue =
        (((aadt_combi / 2) *
          commodity_val_consts.ihcoton *
          commodity_val_consts.ihval +
          (aadt_singl / 2) *
          commodity_val_consts.ihsiton *
          commodity_val_consts.ihval) *
          365) /
        1000000;
      return commodityvalue;
    }
    if ((f_system === 2)) {
      commodityvalue =
        (((aadt_combi / 2) *
          commodity_val_consts.fwycoton *
          commodity_val_consts.fwyval +
          (aadt_singl / 2) *
          commodity_val_consts.fwysiton *
          commodity_val_consts.fwyval) *
          365) /
        1000000;
      return commodityvalue;
    }
    if (f_system > 2) {
      commodityvalue =
        (((aadt_combi / 2) *
          commodity_val_consts.pascoton *
          commodity_val_consts.pasval +
          (aadt_singl / 2) *
          commodity_val_consts.passiton *
          commodity_val_consts.pasval) *
          365) /
        1000000;
      return commodityvalue;
    }
  }
  const [featureCount, setFeatureCount] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // NOTE: this was a useMemo returning a cleanup fn. useMemo does NOT call
  // cleanup returns, and the AbortController's signal was never passed to
  // axios, so there was no stale-response protection at all. Converted to a
  // useEffect with a real `cancelled` guard (late responses from superseded
  // selections are dropped instead of overwriting state) and the signal is now
  // wired into the axios calls so abort actually cancels in-flight requests.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setIsLoading(true);
    setFullHPMSdata([]);

    let cntyCounter = 0;
    let countyIds = [];

    const fetchCountyPromise = async (dataUrl, index) => {
      const countyPromise = await axios.get(dataUrl, {
        signal: controller.signal,
      });
      cntyCounter = cntyCounter + 1;
      if (!cancelled)
        setLoadingProgress((cntyCounter / countyIds.length) * 100);
      return countyPromise;
    };

    if (query.geoRoute === "hpms/geojson") {
      if (query.geography.region !== "None") {
        countyIds = geographic_options.filter(
          (item) =>
            item.text.replace(/\s+/g, "-").toLowerCase() ===
            query.geography.region
        )[0].counties;
      }
      if (query.geography.county !== "None") {
        countyIds = [query.geography.county];
      }
    } else {
      countyIds = [];
    }

    let promises = [];
    let url = "";
    if (countyIds.length > 0) {
      promises = countyIds.map((r, index) => {
        const dataUrl =
          `${process.env.REACT_APP_API_ROOT}` +
          `/roads/findByCounty?county=` +
          r +
          "&year=" +
          query.yearSelected;
        return fetchCountyPromise(dataUrl, index);
      });
    } else {
      url = `${process.env.REACT_APP_API_ROOT}/roads/findByCorridor?lookupTable=${query.segments.segmentsTable}&corridor=${query.segments.segmentSelection}&year=${query.yearSelected}`;
      promises = fetchCountyPromise(url, 0);
    }

    let promiseResult = [];
    if (countyIds.length > 0) {
      promiseResult = Promise.allSettled(promises);
    } else {
      promiseResult = promises;
    }

    let fTemp = [];

    function setRowData(obj) {
      obj.color = COLOR_LINES.normal;
      obj.weight = lineWeights.normal;
      obj.color_base = obj.color;
      obj.weight_base = obj.weight;
      obj.properties.id = obj.id;

      obj.properties.commodity_value1 = setCommodityValueDir(
        obj.properties.f_system,
        obj.properties.aadt_combi,
        obj.properties.aadt_sgl_u
      );
      obj.properties.commodity_value2 = obj.properties.commodity_value1;
      obj.properties.commodity_value =
        obj.properties.commodity_value1 + obj.properties.commodity_value2;

      // Compute delay/mile (not in DB, derived from delay / seg_length)
      const segLen = obj.properties.seg_length;
      obj.properties.dlymile =
        segLen > 0 ? obj.properties.delay / segLen : 0;
      obj.properties.trkdlymile =
        segLen > 0 ? obj.properties.trkdelay / segLen : 0;
      obj.properties.dlymile1 =
        segLen > 0 ? (obj.properties.delay1 ?? 0) / segLen : 0;
      obj.properties.dlymile2 =
        segLen > 0 ? (obj.properties.delay2 ?? 0) / segLen : 0;
      obj.properties.trkdlymile1 =
        segLen > 0 ? (obj.properties.trkdelay1 ?? 0) / segLen : 0;
      obj.properties.trkdlymile2 =
        segLen > 0 ? (obj.properties.trkdelay2 ?? 0) / segLen : 0;

      obj.properties.iri_vmt = obj.properties.iri;
      if (obj.properties.iri === 0) {
        obj.properties.ttivmt_iri = 0;
        obj.properties.lanemiles_iri = 0;
      } else {
        obj.properties.ttivmt_iri = obj.properties.ttivmt;
        obj.properties.lanemiles_iri =
          obj.properties.seg_length * obj.properties.through_la;
      }
      return obj;
    }

    if (countyIds.length > 0) {
      promiseResult
        .then((responses) => {
          // A newer selection superseded this request while it was in flight.
          if (cancelled) return;
          responses.forEach((response, index) => {
            // allSettled entries may be rejected (aborted / failed); a rejected
            // entry has no `.value`, so skip it instead of crashing on .data.
            if (response.status !== "fulfilled" || !response.value) return;
            let f = response.value.data;
            let fc = f.map((row) => {
              return setRowData(row);
            });
            fTemp = fTemp.concat(fc);
          });

          setHpmsGeo({ type: "FeatureCollection", features: fTemp });
          setBounds(fTemp);
          setFullHPMSdata(fTemp);
          setFeatureCount(fTemp.length);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancelled || axios.isCancel(err)) return;
          console.error("County fetch failed:", err);
          setIsLoading(false);
        });
    } else {
      promises
        .then((response) => {
          if (cancelled) return;
          let f = response.data;
          let fc = f.map((row) => {
            return setRowData(row);
          });
          fTemp = fTemp.concat(fc);
          setHpmsGeo({ type: "FeatureCollection", features: fTemp });
          setBounds(fTemp);
          setFullHPMSdata(fTemp);
          setFeatureCount(fTemp.length);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancelled || axios.isCancel(err)) return;
          console.error("Corridor fetch failed:", err);
          setIsLoading(false);
        });
    }

    if (geoJsonLayer.current) {
      geoJsonLayer.current.clearLayers();
      geoJsonLayer.current.addData(hpmsGeo);
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [query.geography.region, query.geography.county, query.yearSelected]);

  useEffect(() => {
    if (fullHPMSdata.length > 0) {
      setIsLoading(true);

      let fc_filter2 = fullHPMSdata;

      query.ownershipFilters.forEach((filterRow) => {
        let filterValue = parseInt(filterRow.id, 10);
        if (!filterRow.checked) {
          return (fc_filter2 = fc_filter2.filter(
            (row) => row.properties.ownership !== filterValue
          ));
        }
      });
      query.fsystemFilters.forEach((filterRow) => {
        let filterValue = parseInt(filterRow.id, 10);
        if (!filterRow.checked) {
          return (fc_filter2 = fc_filter2.filter(
            (row) => row.properties.f_system !== filterValue
          ));
        }
      });
      query.urbanCodeFilters.forEach((filterRow) => {
        let filterValue = parseInt(filterRow.id, 10);
        if (!filterRow.checked) {
          return (fc_filter2 = fc_filter2.filter(
            (row) => row.properties.urban_code !== filterValue
          ));
        }
      });
      query.nhsFilters.forEach((filterRow) => {
        let filterValue = parseInt(filterRow.id, 10);
        if (!filterRow.checked) {
          return (fc_filter2 = fc_filter2.filter(
            (row) => row.properties.nhs !== filterValue
          ));
        }
      });
      query.strahnetFilters.forEach((filterRow) => {
        let filterValue = parseInt(filterRow.id, 10);
        if (!filterRow.checked) {
          return (fc_filter2 = fc_filter2.filter(
            (row) => row.properties.strahnet !== filterValue
          ));
        }
      });
      query.truckNetworkFilters.forEach((filterRow) => {
        let filterValue = parseInt(filterRow.id, 10);
        if (!filterRow.checked) {
          return (fc_filter2 = fc_filter2.filter(
            (row) => row.properties.truck !== filterValue
          ));
        }
      });

      if (fc_filter2.length > 1) {
        if (selectedMapLineCategory !== "none") {
          let tempCategory = selectedMapLineCategory;

          let maxValue = 10000;
          let minValue = 0;

          if (query.vehicleType === "truck") {
            const catMatch = mapLineCategories.find(
              (itm) => itm.all === selectedMapLineCategory
            );
            tempCategory = catMatch ? catMatch.truck : selectedMapLineCategory;
          }

          // dlymile treated as cross-sectional only (like iri, commodity_value)
          if (
            query.directionality === "crossSectional" ||
            selectedMapLineCategory === "iri" ||
            selectedMapLineCategory === "commodity_value" ||
            selectedMapLineCategory === "dlymile"
          ) {
            maxValue = toNum(getTopGeojsonN(fc_filter2, tempCategory, 1));
            minValue = toNum(getBottomGeojsonN(fc_filter2, tempCategory, 1));
            setMinMaxValues({
              min: minValue,
              max: maxValue,
            });
          } else {
            let maxValue1 = toNum(getTopGeojsonN(fc_filter2, tempCategory + "1", 1));
            let minValue1 = toNum(
              getBottomGeojsonNNo0(fc_filter2, tempCategory + "1", 1)
            );
            let maxValue2 = toNum(getTopGeojsonN(fc_filter2, tempCategory + "2", 1));
            let minValue2 = toNum(
              getBottomGeojsonNNo0(fc_filter2, tempCategory + "2", 1)
            );
            maxValue = Math.max(maxValue1, maxValue2);
            minValue = Math.min(minValue1, minValue2);

            setMinMaxValues({
              min: minValue,
              max: maxValue,
            });
          }
          let increm = (maxValue - minValue) * 0.2;
          let fc = [];
          // dlymile treated as cross-sectional only (like iri, commodity_value)
          if (
            query.directionality === "crossSectional" ||
            selectedMapLineCategory === "iri" ||
            selectedMapLineCategory === "commodity_value" ||
            selectedMapLineCategory === "dlymile"
          ) {
            fc = fc_filter2.map((row) => {
              row.color = setMapLineColor(
                row.properties[tempCategory],
                maxValue,
                minValue,
                increm
              );
              row.weight = setMapLineWeight(
                row.properties[tempCategory],
                maxValue,
                minValue,
                increm
              );
              row.color_base = row.color;
              row.weight_base = row.weight;
              return row;
            });
          } else {
            fc = fc_filter2.map((row) => {
              row.color = setMapLineColor(
                Math.max(
                  row.properties[tempCategory + "1"],
                  row.properties[tempCategory + "2"]
                ),
                maxValue,
                minValue,
                increm
              );
              row.weight = setMapLineWeight(
                row.properties[tempCategory],
                maxValue,
                minValue,
                increm
              );
              row.color_base = row.color;
              row.weight_base = row.weight;
              return row;
            });
          }
        } else {
          // No metric selected= clear bounds so a previous focus's min/max
          // doesn't linger and get used for coloring.
          setMinMaxValues({ min: 0, max: 0 });
          let fc = fc_filter2.map((row) => {
            row.color = COLOR_LINES.normal;
            row.weight = lineWeights.normal;
            row.color_base = row.color;
            row.weight_base = row.weight;
            return row;
          });
        }
      }

      setHpmsGeo({ type: "FeatureCollection", features: fc_filter2 });
      if (geoJsonLayer.current) {
        geoJsonLayer.current.clearLayers();
        geoJsonLayer.current.addData({
          type: "FeatureCollection",
          features: fc_filter2,
        });
      }

      setFeatureCount(fc_filter2.length);
      if (fc_filter2.length > 0) setBounds(fc_filter2);

      setIsLoading(false);
    }
  }, [
    query.ownershipFilters,
    query.fsystemFilters,
    query.urbanCodeFilters,
    query.nhsFilters,
    query.strahnetFilters,
    query.truckNetworkFilters,
    query.yearSelected,
    fullHPMSdata,
  ]);

  const getHPMSStyle = (feature) => {
    return { color: feature.color, weight: feature.weight };
  };

  const handleMouseOver = (feature, e) => {
    const layer = e.target;
    hpmsColorContainer.current = feature.color;
    hpmsWeightContainer.current = feature.weight;
    feature.color = COLOR_LINES.selected;
    feature.weight = lineWeights.selected;
    layer.setStyle({
      weight: feature.weight,
      color: feature.color,
      dashArray: "",
      fillOpacity: 0.7,
    });
    setInfo(feature.properties);
  };

  const handleMouseOut = (feature, e) => {
    const layer = e.target;
    feature.color = hpmsColorContainer.current;
    feature.weight = hpmsWeightContainer.current;
    layer.setStyle({
      weight: feature.weight,
      color: feature.color,
      dashArray: "",
      fillOpacity: 0.7,
    });
  };

  const handleClickSegment = (feature, layer, e) => {
    let fromTo = `${feature.properties.beg_point} to ${feature.properties.end_point}`;
    setPopupTitle(
      <div>
        <p className="left-text" style={{ marginBottom: 1 }}>
          <strong>Route ID:</strong> {feature.properties.route_id}
        </p>
        <p className="left-text" style={{ marginTop: 1 }}>
          <strong>From/To:</strong> {fromTo}
        </p>
      </div>
    );
  };

  const defaultUserSelection = {
    from_route_id: "",
    from_beg_point: Number.NaN,
    from_end_point: Number.NaN,
    to_beg_point: Number.NaN,
    to_end_point: Number.NaN,
  };

  const userSelection = useRef(defaultUserSelection);

  const resetUserSelection = (route_id) => {
    hpmsGeo.features
      .filter((f) => f.properties.route_id === route_id)
      .forEach((f) => {
        f.color = f.color_base;
        f.weight = f.weight_base;
      });
    userSelection.current = defaultUserSelection;
  };

  const highlightUserSelection = () => {
    const selection = userSelection.current;

    hpmsGeo.features
      .filter((f) => f.properties.route_id === selection.from_route_id)
      .forEach((f) => {
        f.weight = 7;
      });

    hpmsGeo.features
      .filter((f) => f.properties.route_id !== selection.from_route_id)
      .forEach((f) => {
        f.weight = 0.1;
      });

    if (!isNaN(selection.to_end_point)) {
      const fromDfo = Math.min(
        selection.from_beg_point,
        selection.to_beg_point
      );
      const toDfo = Math.max(selection.from_end_point, selection.to_end_point);
      hpmsGeo.features
        .filter(
          (f) =>
            f.properties.route_id === selection.from_route_id &&
            f.properties.beg_point >= fromDfo &&
            f.properties.end_point <= toDfo
        )
        .forEach((f) => {
          f.weight = 7;
        });
    } else {
      hpmsGeo.features
        .filter(
          (f) =>
            f.properties.route_id === selection.from_route_id &&
            f.properties.beg_point === selection.from_beg_point
        )
        .forEach((f) => {
          f.weight = 7;
        });
    }

    setGeoJsonKey(Date.now());
  };

  function handleAddSegment() {
    let featureProps = popupRef.current._source.feature.properties;

    if (
      query.geoRoute === "hpms/geojson" ||
      query.geoRoute === "hpms_segments/geojson"
    ) {
      dispatch({
        type: "setUserSelection",
        selection: {
          route_id: featureProps.route_id,
          beg_point: featureProps.beg_point,
          end_point: featureProps.end_point,
        },
      });
      dispatch({
        type: "setUserSelectionMD100",
        selection: { route_id: "", beg_point: NaN, end_point: NaN },
      });
      dispatch({
        type: "setUserSelectedSegments",
        selection: query.userSelectedSegments.concat({
          route_id: featureProps.route_id,
          beg_point: featureProps.beg_point,
          end_point: featureProps.end_point,
          mergekey:
            featureProps.route_id +
            "_" +
            featureProps.beg_point +
            "_" +
            featureProps.end_point,
        }),
      });
    } else {
      dispatch({
        type: "setUserSelection",
        selection: { route_id: "", beg_point: NaN, end_point: NaN },
      });
      dispatch({
        type: "setUserSelectionMD100",
        selection: {
          route_id: featureProps.seg_name,
          beg_point: featureProps.seg_from,
          end_point: featureProps.seg_to,
        },
      });
    }
    popupRef.current.remove();
  }

  function handleFrom() {
    let featureProps = popupRef.current._source.feature.properties;
    resetUserSelection(userSelection.current.from_route_id);
    userSelection.current = {
      from_route_id: featureProps.route_id,
      from_beg_point: featureProps.beg_point,
      from_end_point: featureProps.end_point,
    };
    highlightUserSelection();
    popupRef.current.remove();
  }

  function handleTo() {
    let featureProps = popupRef.current._source.feature.properties;
    let newSelection = { ...userSelection.current };
    newSelection.to_beg_point = featureProps.beg_point;
    newSelection.to_end_point = featureProps.end_point;
    userSelection.current = newSelection;

    const fromDfo = Math.min(
      newSelection.from_beg_point,
      newSelection.to_beg_point
    );
    const toDfo = Math.max(
      newSelection.from_end_point,
      newSelection.to_end_point
    );

    dispatch({
      type: "setUserSelection",
      selection: {
        route_id: newSelection.from_route_id,
        beg_point: fromDfo,
        end_point: toDfo,
      },
    });
    dispatch({
      type: "setUserSelectionMD100",
      selection: { route_id: "", beg_point: NaN, end_point: NaN },
    });
    dispatch({
      type: "setUserSelectedSegments",
      selection: query.userSelectedSegments.concat({
        route_id: newSelection.from_route_id,
        beg_point: fromDfo,
        end_point: toDfo,
        mergekey: newSelection.from_route_id + "_" + fromDfo + "_" + toDfo,
      }),
    });

    highlightUserSelection();
    hpmsGeo.features.forEach((f) => {
      f.color = f.color_base;
      f.weight = f.weight_base;
    });
    popupRef.current.remove();
    setGeoJsonKey(Date.now());
  }

  function handleClearSelection() {
    hpmsGeo.features.forEach((f) => {
      f.color = f.color_base;
      f.weight = f.weight_base;
    });
    popupRef.current.remove();
    setGeoJsonKey(Date.now());
  }

  const onEachFeature = (feature, layer) => {
    layer.on({
      mouseover: handleMouseOver.bind(null, feature),
      mouseout: handleMouseOut.bind(null, feature),
      click: handleClickSegment.bind(null, feature, layer),
    });
  };

  const { BaseLayer, Overlay } = LayersControl;
  const [mapBounds, setMapBounds] = useState(props.bounds);
  const [mapZoom, setMapZoom] = useState(props.Zoom);

  function returnHPMSVariable(name) {
    for (var i = SUMMARY_VARIABLES_HPMS.length - 1; i >= 0; i--) {
      if (
        name === SUMMARY_VARIABLES_HPMS[i].variable ||
        name.substring(0, name.length - 1) ===
        SUMMARY_VARIABLES_HPMS[i].variable
      ) {
        return SUMMARY_VARIABLES_HPMS[i];
      }
    }
  }

  function PopupData(props) {
    return (
      <div>
        <ul>
          {POPUP_GROUPS[props.type].map((value, index) => {
            const field = returnHPMSVariable(value);
            return (
              <li key={index}>
                {field.summaryName}:{" "}
                {info[value] === null
                  ? ""
                  : info[value].toLocaleString("en-US", field.formatOptions)}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  let toFrom = "";
  let routeId = "";
  if (typeof info.route_id !== "undefined") {
    toFrom = `${info.beg_point.toLocaleString("en-US", {
      maximumFractionDigits: 2,
    })} to ${info.end_point.toLocaleString("en-US", {
      maximumFractionDigits: 2,
    })}`;
    routeId = info.route_id;
  } else if (typeof info.seg_name !== "undefined") {
    toFrom = `${info.seg_from} to ${info.seg_to}`;
    routeId = info.seg_name;
  }

  const popupRef = useRef(false);

  function handlePopupOpen(a, b, c) {
    let featureProps = popupRef.current._source.feature.properties;
    if (
      query.geoRoute === "hpms/geojson" ||
      query.geoRoute === "hpms_segments/geojson"
    ) {
      setPopupTitle(
        `Road: ${featureProps.properties.route_id} (${featureProps.beg_point.toLocaleString(
          "en-US",
          { maximumFractionDigits: 2 }
        )} to ${featureProps.end_point.toLocaleString("en-US", {
          maximumFractionDigits: 2,
        })})`
      );
    } else {
      setPopupTitle(
        `Road: ${featureProps.seg_name} (${featureProps.seg_from} to ${featureProps.seg_to})`
      );
    }
  }
  const [popupTitle, setPopupTitle] = useState("untitled");

  const geoJsonLayer = useRef(null);
  const [geoJsonLayerRef, setGeoJsonLayerRef] = useState(null);

  useEffect(() => {
    if (geoJsonLayer.current) {
      geoJsonLayer.current.clearLayers();
      geoJsonLayer.current.addData(hpmsGeo);
    }

    let fTempProperties = [];
    let tempData = hpmsGeo.features;
    tempData.map((x) => fTempProperties.push(x.properties));

    dispatch({
      type: "setFeatures",
      selection: fTempProperties,
    });
  }, [hpmsGeo]);

  const layerRef = useRef();

  const POSITION_CLASSES = {
    bottomleft: "leaflet-bottom leaflet-left",
    bottomright: "leaflet-bottom leaflet-right",
    topleft: "leaflet-top leaflet-left",
    topright: "leaflet-top leaflet-right",
  };

  useEffect(() => {
    if (map) map.fitBounds(mapBounds);
  }, [mapBounds]);

  function MapResetControl({ position, map }) {
    const positionClass =
      (position && POSITION_CLASSES[position]) || POSITION_CLASSES.topright;

    const onClick = useCallback(() => {
      map.fitBounds(mapBounds);
    }, [map]);

    return (
      <div className={positionClass}>
        <div className="resetmapcontrol">
          <div
            className="leaflet-control leaflet-bar"
            style={{ borderStyle: "none" }}
          >
            <IconButton
              className="home-button"
              color="inherit"
              aria-label="reset map"
              title="Reset Map"
              edge="start"
              onClick={onClick}
            >
              <Icon>home</Icon>
            </IconButton>
          </div>
        </div>
      </div>
    );
  }

  const mapLineCategories = [
    { description: "None", all: "none", truck: "none" },
    { description: "Delay/mile", all: "dlymile", truck: "trkdlymile" }, // NEW
    { description: "Delay", all: "delay", truck: "trkdelay" },
    { description: "TTI", all: "tti", truck: "trktti" },
    { description: "PTI 95", all: "pti95", truck: "trkpti95" },
    { description: "TTTR - Est.", all: "tttrest", truck: "tttrest" },
    { description: "IRI", all: "iri", truck: "iri" },
    {
      description: "Commodity Value",
      all: "commodity_value",
      truck: "commodity_value",
    },
    { description: "Congested CO2 (Tons)", all: "ghg", truck: "ghgtrk" },
    {
      description: "Normal CO2 (Tons)",
      all: "ghgbase",
      truck: "ghgtrkbase",
    },
  ];

  const [selectedMapLineCategory, setSelectedMapLineCategory] =
    useState("none");

  // Guard against an invalid geoRoute/mapFocus pair during quick selection
  // changes: filter(...) can return [] mid-update, so reading [0].all would
  // throw. Fall back to "none" and depend on both fields that are read here.
  useEffect(() => {
    const match = mapFocusCategories.find(
      (itm) =>
        itm.category === query.geoRoute && itm.description === query.mapFocus
    );
    setSelectedMapLineCategory(match ? match.all : "none");
  }, [query.mapFocus, query.geoRoute]);

  // Coerce a possibly-null/NaN bound to a safe number so stale/undefined values
  // never reach minMaxValues or the color/weight math.
  const toNum = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : 0);

  function getTopJsonN(arr, prop, n) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    var clone = arr.slice(0);
    let selectedRow = clone.sort((a, b) => b[prop] - a[prop]).slice(0, n || 1);
    return selectedRow[0]?.[prop] ?? null;
  }
  function getBottomJsonN(arr, prop, n) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    var clone = arr.slice(0);
    let selectedRow = clone.sort((a, b) => a[prop] - b[prop]).slice(0, n || 1);
    return selectedRow[0]?.[prop] ?? null;
  }
  function getBottomJsonNNo0(arr, prop, n) {
    if (!Array.isArray(arr)) return null;
    var clone = arr.filter((itm) => itm[prop] > 0).slice(0);
    if (clone.length === 0) return null;
    let selectedRow = clone.sort((a, b) => a[prop] - b[prop]).slice(0, n || 1);
    return selectedRow[0]?.[prop] ?? null;
  }
  function getTopGeojsonN(arr, prop, n) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    var clone = arr.slice(0);
    let selectedRow = clone
      .sort((a, b) => b.properties[prop] - a.properties[prop])
      .slice(0, n || 1);
    return selectedRow[0]?.properties?.[prop] ?? null;
  }
  function getBottomGeojsonN(arr, prop, n) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    var clone = arr.slice(0);
    let selectedRow = clone
      .sort((a, b) => a.properties[prop] - b.properties[prop])
      .slice(0, n || 1);
    return selectedRow[0]?.properties?.[prop] ?? null;
  }
  function getBottomGeojsonNNo0(arr, prop, n) {
    if (!Array.isArray(arr)) return null;
    var clone = arr.filter((itm) => itm.properties[prop] > 0).slice(0);
    if (clone.length === 0) return null;
    let selectedRow = clone
      .sort((a, b) => a.properties[prop] - b.properties[prop])
      .slice(0, n || 1);
    return selectedRow[0]?.properties?.[prop] ?? null;
  }

  const [minMaxValues, setMinMaxValues] = useState({
    min: 0,
    max: 0,
  });

  useEffect(() => {
    if (hpmsGeo.features.length > 0) {
      if (selectedMapLineCategory !== "none") {
        let tempCategory = selectedMapLineCategory;
        let maxValue = 10000;
        let minValue = 0;

        if (query.vehicleType === "truck") {
          const catMatch = mapLineCategories.find(
            (itm) => itm.all === selectedMapLineCategory
          );
          tempCategory = catMatch ? catMatch.truck : selectedMapLineCategory;
        }

        // dlymile treated as cross-sectional only (like iri, commodity_value)
        if (
          query.directionality === "crossSectional" ||
          selectedMapLineCategory === "iri" ||
          selectedMapLineCategory === "commodity_value" ||
          selectedMapLineCategory === "dlymile"
        ) {
          maxValue = toNum(getTopGeojsonN(hpmsGeo.features, tempCategory, 1));
          minValue = toNum(getBottomGeojsonN(hpmsGeo.features, tempCategory, 1));
          setMinMaxValues({ min: minValue, max: maxValue });
        } else {
          let maxValue1 = toNum(
            getTopGeojsonN(hpmsGeo.features, tempCategory + "1", 1)
          );
          let minValue1 = toNum(
            getBottomGeojsonNNo0(hpmsGeo.features, tempCategory + "1", 1)
          );
          let maxValue2 = toNum(
            getTopGeojsonN(hpmsGeo.features, tempCategory + "2", 1)
          );
          let minValue2 = toNum(
            getBottomGeojsonNNo0(hpmsGeo.features, tempCategory + "2", 1)
          );
          maxValue = Math.max(maxValue1, maxValue2);
          minValue = Math.min(minValue1, minValue2);
          setMinMaxValues({
            min: minValue,
            max: maxValue,
          });
        }

        let increm = (maxValue - minValue) * 0.2;
        let fc = [];

        // dlymile treated as cross-sectional only (like iri, commodity_value)
        if (
          query.directionality === "crossSectional" ||
          selectedMapLineCategory === "iri" ||
          selectedMapLineCategory === "commodity_value" ||
          selectedMapLineCategory === "dlymile"
        ) {
          fc = hpmsGeo.features.map((row) => {
            row.color = setMapLineColor(
              row.properties[tempCategory],
              maxValue,
              minValue,
              increm
            );
            row.weight = setMapLineWeight(
              row.properties[tempCategory],
              maxValue,
              minValue,
              increm
            );
            row.color_base = row.color;
            row.weight_base = row.weight;
            return row;
          });
        } else {
          fc = hpmsGeo.features.map((row) => {
            row.color = setMapLineColor(
              Math.max(
                row.properties[tempCategory + "1"],
                row.properties[tempCategory + "2"]
              ),
              maxValue,
              minValue,
              increm
            );
            row.weight = setMapLineWeight(
              row.properties[tempCategory],
              maxValue,
              minValue,
              increm
            );
            row.color_base = row.color;
            row.weight_base = row.weight;
            return row;
          });
        }
        setHpmsGeo({ type: "FeatureCollection", features: fc });
        setGeoJsonKey(Date.now());
      } else {
        // No metric selected -> clear bounds so a previous focus's min/max
        // doesn't linger.
        setMinMaxValues({ min: 0, max: 0 });
        let fc = hpmsGeo.features.map((row) => {
          row.color = COLOR_LINES.normal;
          row.weight = lineWeights.normal;
          row.color_base = row.color;
          row.weight_base = row.weight;
          return row;
        });
        setHpmsGeo({ type: "FeatureCollection", features: fc });
        setGeoJsonKey(Date.now());
      }
    }
  }, [
    query.geoRoute,
    query.vehicleType,
    selectedMapLineCategory,
    query.directionality,
  ]);

  useEffect(() => {
    if (query.userSelectedRoads.length > 0) {
      hpmsGeo.features
        .filter((f) => query.userSelectedRoads.indexOf(f.id) === -1)
        .forEach((f) => { f.weight = 0.1; });
      hpmsGeo.features
        .filter((f) => query.userSelectedRoads.indexOf(f.id) > -1)
        .forEach((f) => { f.weight = f.weight_base * 2; });
      setBounds(
        hpmsGeo.features.filter(
          (f) => query.userSelectedRoads.indexOf(f.id) > -1
        )
      );
      setGeoJsonKey(Date.now());
    } else {
      hpmsGeo.features.forEach((f) => { f.weight = f.weight_base; });
      setBounds(hpmsGeo.features);
      setGeoJsonKey(Date.now());
    }
  }, [query.userSelectedRoads]);

  function setMapLineColor(value, maxValue, minValue, increm) {
    if (value >= minValue + increm * 4) return "red";
    if (value >= minValue + increm * 3 && value < minValue + increm * 4)
      return "orange";
    if (value >= minValue + increm * 2 && value < minValue + increm * 3)
      return "yellow";
    if (value >= minValue + increm * 1 && value < minValue + increm * 2)
      return "blue";
    return "green";
  }

  function setMapLineWeight(value, maxValue, minValue, increm) {
    if (value >= minValue + increm * 4) return 6;
    if (value >= minValue + increm * 3 && value < minValue + increm * 4)
      return 4.8;
    if (value >= minValue + increm * 2 && value < minValue + increm * 3)
      return 3.6;
    if (value >= minValue + increm * 1 && value < minValue + increm * 2)
      return 2.4;
    return 1.5;
  }

  useEffect(() => {
    if (map)
      setTimeout(function () {
        map.invalidateSize();
      }, 185);
  }, [openDrawer]);

  const crabLayerRef = useRef();
  const ctpLayerRef = useRef();
  const ShaProjectsLayerRef = useRef();
  const LandUse2010LayerRef = useRef();

  const layerIcons = {
    ctp: renderToStaticMarkup(
      <ConstructionIcon style={{ fontSize: 15, color: "#0047AB" }} />
    ),
    shaProjects: renderToStaticMarkup(
      <ConstructionIcon style={{ fontSize: 15, color: "#FFC300" }} />
    ),
    airports: renderToStaticMarkup(
      <FlightIcon style={{ fontSize: 16, color: "#FFFFFF", backgroundColor: "#A9A9A9" }} />
    ),
    truckWeighInspStations: renderToStaticMarkup(
      <LocalShippingIcon style={{ fontSize: 20, color: "#000000", backgroundColor: "#CBC3E3" }} />
    ),
    distributionCenters: renderToStaticMarkup(
      <BusinessIcon style={{ fontSize: 20, color: "#000000", backgroundColor: "#90ee90" }} />
    ),
    autoTerminals: renderToStaticMarkup(
      <DirectionsCarIcon style={{ fontSize: 20, color: "#000000", backgroundColor: "#add8e6" }} />
    ),
    cargoAirports: renderToStaticMarkup(
      <FlightIcon style={{ fontSize: 16, color: "#FFFF00", backgroundColor: "#000000" }} />
    ),
    intermodalFacilities: renderToStaticMarkup(
      <Icon style={{ fontSize: 16, color: "#ffffff", backgroundColor: "#11b461" }} />
    ),
    otherTerminals: renderToStaticMarkup(
      <Icon style={{ fontSize: 16, color: "#ffffff", backgroundColor: "#FFFF00" }} />
    ),
    ports: renderToStaticMarkup(
      <AnchorIcon style={{ fontSize: 16, color: "#ffffff", backgroundColor: "#0000ff" }} />
    ),
    tollPlazas: renderToStaticMarkup(
      <TollIcon style={{ fontSize: 16, color: "#ffffff", backgroundColor: "#ff7800" }} />
    ),
    fhwaTruckParking: renderToStaticMarkup(
      <LocalParkingIcon style={{ fontSize: 16, color: "#000000", backgroundColor: "#add8e6" }} />
    ),
  };

  return (
    <MapContainer
      bounds={mapBounds}
      zoom={mapZoom}
      minZoom={8}
      attributionContol={true}
      scrollWheelZoom={true}
      ref={setMap}
      zoomControl={false}
    >
      <LoadingOverlay
        active={fullHPMSdata.length > 0 ? false : true}
        spinner={
          <div>
            <BounceLoader />
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              className={classes.linearProgress}
            />
          </div>
        }
        text={
          query.statsLayerDesc
            ? "Loading stats layer..."
            : "Please select a layer from the menu on the left..."
        }
      >
        <ZoomControl position="topright" />
        <MapResetControl position="topright" map={map} />

        <LayersControl position="topright">
          <BaseLayer checked name="Streets" key={"mapbox.streets"}>
            <TileLayer
              url={process.env.REACT_APP_MAPBOX_STREETS_V10_URL}
              attribution={process.env.REACT_APP_MAPBOX_ATTRIBUTION}
              id="mapbox.streets"
              access_token={process.env.REACT_APP_MAPBOX_ACCESSTOKEN}
            />
          </BaseLayer>
          <BaseLayer name="Satellite">
            <TileLayer
              url={process.env.REACT_APP_MAPBOX_URL}
              attribution={process.env.REACT_APP_MAPBOX_ATTRIBUTION}
              id="mapbox/satellite-v9"
              access_token={process.env.REACT_APP_MAPBOX_ACCESSTOKEN}
            />
          </BaseLayer>
          <BaseLayer name="Satellite-Streets">
            <TileLayer
              url={process.env.REACT_APP_MAPBOX_URL}
              attribution={process.env.REACT_APP_MAPBOX_ATTRIBUTION}
              id="mapbox/satellite-streets-v11"
              access_token={process.env.REACT_APP_MAPBOX_ACCESSTOKEN}
            />
          </BaseLayer>
          <BaseLayer name="Light">
            <TileLayer
              url={process.env.REACT_APP_MAPBOX_URL}
              attribution={process.env.REACT_APP_MAPBOX_ATTRIBUTION}
              id="mapbox/light-v10"
              access_token={process.env.REACT_APP_MAPBOX_ACCESSTOKEN}
            />
          </BaseLayer>
          <BaseLayer name="Dark">
            <TileLayer
              url={process.env.REACT_APP_MAPBOX_URL}
              attribution={process.env.REACT_APP_MAPBOX_ATTRIBUTION}
              id="mapbox/dark-v10"
              access_token={process.env.REACT_APP_MAPBOX_ACCESSTOKEN}
            />
          </BaseLayer>
          {hpmsGeo.features.length > 0 && (
            <Overlay checked name="Roads" style={{ zIndex: 500 }}>
              <GeoJSON
                data={hpmsGeo}
                style={getHPMSStyle}
                onEachFeature={onEachFeature}
                ref={geoJsonLayer}
                whenCreated={setGeoJsonLayerRef}
              >
                <Popup ref={popupRef}>
                  <div className="custom-block">
                    {popupTitle}
                    <div>
                      <p>
                        To add road segment to the data table, click{" "}
                        <strong>Add segment</strong> .
                      </p>
                      <p className="from-to-links">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleAddSegment}
                        >
                          Add segment
                        </button>
                      </p>
                    </div>
                    {(query.geoRoute === "hpms/geojson" ||
                      query.geoRoute === "hpms_segments/geojson") && (
                        <div>
                          <div>
                            <p>To add a corridor to the data table:</p>
                            <ol>
                              <li>
                                Click <strong>From Here</strong> on first segment
                              </li>
                              <li>
                                Click <strong>To Here</strong> on final segment to
                                add corridor to data table.
                              </li>
                            </ol>
                          </div>
                          <p className="from-to-links">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={handleFrom}
                            >
                              From here
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleTo}
                            >
                              To here
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleClearSelection}
                            >
                              Clear selection
                            </button>
                          </p>
                        </div>
                      )}
                  </div>
                </Popup>
              </GeoJSON>
            </Overlay>
          )}
          <Overlay
            name={"FHWA Truck Parking 2019 " + layerIcons.fhwaTruckParking}
            style={{ zIndex: 500 }}
          >
            <TruckParkingLayer layerIcon={layerIcons.fhwaTruckParking} />
          </Overlay>
          <Overlay
            name={"Cargo Airports " + layerIcons.cargoAirports}
            style={{ zIndex: 495 }}
          >
            <CargoAirportsLayer layerIcon={layerIcons.cargoAirports} />
          </Overlay>
          <Overlay
            name={"Auto Terminals " + layerIcons.autoTerminals}
            style={{ zIndex: 494 }}
          >
            <AutoTerminalsLayer layerIcon={layerIcons.autoTerminals} />
          </Overlay>
          <Overlay
            name={"Distribution Centers " + layerIcons.distributionCenters}
            style={{ zIndex: 493 }}
          >
            <DistributionCentersLayer
              layerIcon={layerIcons.distributionCenters}
            />
          </Overlay>
          <Overlay
            name={
              "Truck Weigh/Inspection Stations " +
              layerIcons.truckWeighInspStations
            }
            style={{ zIndex: 492 }}
          >
            <TruckWeighInspStationsLayer
              layerIcon={layerIcons.truckWeighInspStations}
            />
          </Overlay>
          <Overlay
            name={"Airports " + layerIcons.airports}
            style={{ zIndex: 491 }}
          >
            <AirportsLayer layerIcon={layerIcons.airports} />
          </Overlay>
          <Overlay name="MDOT SHA County Boundaries" style={{ zIndex: 590 }}>
            <CountyBoundaryLayer />
          </Overlay>
          <Overlay name="MDOT SHA District Boundaries" style={{ zIndex: 590 }}>
            <DistrictBoundaryLayer />
          </Overlay>
          <Overlay
            name={
              "Consolidated Transportation Program (CTP) FY2022 - FY2027 " +
              layerIcons.ctp
            }
            style={{ zIndex: 590 }}
          >
            <CtpLayer layerRef={ctpLayerRef} iconMarkup={layerIcons.ctp} />
          </Overlay>
          <Overlay
            name={"MDOT SHA Projects " + layerIcons.shaProjects}
            style={{ zIndex: 590 }}
          >
            <ShaProjectsLayer
              layerRef={ShaProjectsLayerRef}
              iconMarkup={layerIcons.shaProjects}
            />
          </Overlay>
        </LayersControl>
      </LoadingOverlay>
      <QuickInfoControl
        position="bottomleft"
        geoRoute={query.geoRoute}
        mapLineCategories={mapLineCategories}
        selectedMapLineCategory={selectedMapLineCategory}
        setSelectedMapLineCategory={setSelectedMapLineCategory}
        checked={checked}
        setChecked={setChecked}
        minMaxValues={minMaxValues}
        info={info}
        routeId={routeId}
        toFrom={toFrom}
        PopupData={PopupData}
 activeOverlays={activeOverlays}
  activeBaseLayer={activeBaseLayer}
  mode={mode}
      />
    </MapContainer>
  );
}

export default LeafletMapState;